/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	OnDestroy,
	OnChanges,
	ViewChild,
	ChangeDetectorRef,
	ElementRef,
} from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
	AbstractControl,
	UntypedFormArray,
	ValidationErrors,
	UntypedFormControl,
	FormArray,
	FormControl,
} from '@angular/forms';
import * as appStrings from '@touchplan/strings';
import { Role } from '@touchplan/common';
import { MatDialog } from '@angular/material/dialog';
import { PlanState } from 'src/app/services/plan-state.service';
import { map, takeUntil, switchMap, filter, startWith, pairwise } from 'rxjs/operators';
import { MatSelectionList } from '@angular/material/list';
import * as _ from 'lodash-es';
import {
	AbstractAddEditItemComponent,
	IButtonPanelEventData,
} from 'src/app/common/dialog-reusable/dialog-reusable.module';
import { CustomFieldDefinition } from 'src/app/app-data/models/CustomFieldDefinition';
import { CustomFieldType } from 'src/app/app-data/state/CustomFieldsApi';
import { CustomFieldService } from '../services/custom-field.services';
import { BehaviorSubject, of, defer, Subscription, Observable } from 'rxjs';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
	CustomFieldDropDownDefinition,
	ICustomFieldDropDownItemDefinition,
} from 'src/app/app-data/models/CustomFieldDropDownDefinition';
import { diff } from 'deep-object-diff';

@Component({
	selector: 'add-edit-custom-field',
	templateUrl: './add-edit-custom-field.component.html',
	styleUrls: ['./add-edit-custom-field.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddEditCustomFieldComponent
	extends AbstractAddEditItemComponent<CustomFieldDefinition>
	implements OnInit, OnDestroy, OnChanges
{
	// select/deselect all
	@ViewChild('allRoleOptions') private allRolesList: MatSelectionList;
	@ViewChild('allTicketTypes') private allTicketList: MatSelectionList;
	@ViewChild('fieldLabel') private fieldLabel: ElementRef;

	public readonly FIELD_ROLE_ITEM_HEIGHT = 48; // pixels

	public allTicketsIndeterminate = false;
	public isEnabledControl: AbstractControl;
	public isRequiredControl: AbstractControl;
	public newChoiceControl: UntypedFormControl; // this one should be by itself because it should not make the form invalid

	private dropdownDefinitions: ICustomFieldDropDownItemDefinition[];

	public strings = appStrings;
	public selectedTabIndex = 0;
	private allRoles: Role[];
	private liveSubscription: Subscription;
	private inputItem$: BehaviorSubject<CustomFieldDefinition>; //todo, fix type

	public dataTypeKeys$: Observable<CustomFieldType[]>;

	constructor(
		public customFieldService: CustomFieldService,
		public planState: PlanState,
		protected dialog: MatDialog,
		protected builder: UntypedFormBuilder,
		private changeDetectorRef: ChangeDetectorRef
	) {
		super(dialog);

		this.planState.rolesApi.data.list$
			.pipe(
				map((roleMap) => Array.from(roleMap.values())),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((roles) => {
				this.allRoles = roles;
			});

		this.newChoiceControl = this.builder.control('', {
			validators: [Validators.maxLength(this.customFieldService.MaxLabelLength)],
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();

		this.dataTypeKeys$ = of(this.customFieldService.DataTypesKeys);

		this.customFieldService.editorActionSubject$
			.pipe(
				filter((action) => !!action),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((action) => {
				if (
					((action.type === 'Cancel' && !this.customFieldService.isAddingNewDefinition(action.item)) ||
						action.type === 'Save') &&
					action.status
				) {
					if (action.type === 'Save') {
						this.itemFormGroup.markAsPristine();
					}
					this.updateFormGroup(action.item);
				}
			});
	}

	/** sets up the listeners for dropdown data */
	private dropdownListInit() {
		this.inputItem$ = new BehaviorSubject(null);
		this.inputItem$
			.pipe(
				switchMap((field) => {
					if (!field) {
						return of(null);
					}
					return this.planState.customFieldsApi.getOrderedDropdownDefinition$().pipe(
						// debounceTime(100), //commented this one for now, tend to think quick initial loading is useful here
						map((list: CustomFieldDropDownDefinition[]) =>
							list.find((def) => def.$id === field.view.listOfValuesId)
						),
						map((definition) => (definition?.view?.data ? definition.view.data : [])) // safe ref here
					);
				})
			)
			.subscribe((definition) => {
				//best case would be not subscribe here, and let angular do it. That's a lot more refactoring and difficultly though.
				this.dropdownDefinitions = definition;
				this.updateDropdownChoiceArray();
			});
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();
		this.inputItem$.complete();
	}

	public ngOnChanges(changes): void {
		super.ngOnChanges(changes);

		//because for who knows what reason angular decided that onInit should be the *second* lifecycle hook called <_<
		if (changes.item?.firstChange) {
			this.dropdownListInit();
		}
		if (changes.item?.currentValue) {
			this.inputItem$.next(changes.item.currentValue);
		}
	}

	public gotoTab(tabIndex: number) {
		this.selectedTabIndex = tabIndex;
	}

	// When adding new custom field, focus and select label value so that user can type right away to change it
	public selectToEditLabel() {
		if (this.fieldLabel && this.item && this.customFieldService.isAddingNewDefinition(this.item)) {
			this.fieldLabel.nativeElement.select();
		}
	}

	protected createFormGroup(field: CustomFieldDefinition): UntypedFormGroup {
		this.itemFormGroup = this.builder.group({
			itemInfoGroup: this.builder.group({
				label: [
					field ? field.view.label : '',
					{ validators: [Validators.required, Validators.maxLength(this.customFieldService.MaxLabelLength)] },
				],
				type: [field ? field.view.type : '', Validators.required],
				unit: [field && field.view.unit ? field.view.unit : '', null],
				ticketTypes: [field ? field.view.ticketTypes : ['task'], Validators.required],
				isEnabled: [field ? field.view.isEnabled : true, Validators.required],
				isRequired: [field ? field.view.isRequired : false],
				roles: [field ? (field.view.roles ? field.view.roles : []) : this.allRoles.map((role) => role.$id)],
				itemAllTicketsControl: [this.initToggleAllTicketTypesControl(field)],
			}),
		});

		// this control is in the button panel, not with others in the form
		this.isEnabledControl = this.itemFormGroup.get('itemInfoGroup').get('isEnabled');
		this.isRequiredControl = this.itemFormGroup.get('itemInfoGroup').get('isRequired');

		// responding to changes
		this.handleFormDataChange();

		return this.itemFormGroup;
	}

	protected updateFormGroup(field: CustomFieldDefinition) {
		// reflect the values saved in the database
		this.itemFormGroup.patchValue(
			{
				itemInfoGroup: {
					label: field ? field.view.label : '',
					type: field ? field.view.type : '',
					unit: field ? field.view.unit : '',
					ticketTypes: field ? field.view.ticketTypes : ['task'],
					isEnabled: field ? field.view.isEnabled : true,
					isRequired: field ? field.view.isRequired : false,
					roles: field ? (field.view.roles ? field.view.roles : []) : this.allRoles.map((role) => role.$id),
					itemAllTicketsControl: this.initToggleAllTicketTypesControl(field),
				},
			},
			{ emitEvent: false }
		);

		// maintain that certain fields of existing one cannot be changed
		if (field) {
			if (this.customFieldService.isAddingNewDefinition(field)) {
				// make sure these are enabled when adding new definition
				this.itemFormGroup.get('itemInfoGroup').get('type').enable({ emitEvent: false });
				this.itemFormGroup.get('itemInfoGroup').get('unit').enable({ emitEvent: false });
			} else {
				// disable certain fields for existing one as needed
				this.itemFormGroup.get('itemInfoGroup').get('type').disable({ emitEvent: false });
				this.itemFormGroup.get('itemInfoGroup').get('unit').disable({ emitEvent: false });
			}
		}

		// this is the start of form editing
		this.itemFormGroup.markAsPristine();
	}

	// override to enable save when we are in adding a new definition that is not saved yet case
	public canSave(): boolean {
		return (
			this.itemFormGroup &&
			this.itemFormGroup.valid &&
			(this.itemFormGroup.dirty || (this.item && this.customFieldService.isAddingNewDefinition(this.item)))
		);
	}

	public canCancel(): boolean {
		return (
			this.itemFormGroup &&
			(this.itemFormGroup.dirty || (this.item && this.customFieldService.isAddingNewDefinition(this.item)))
		);
	}

	private initToggleAllTicketTypesControl(field: CustomFieldDefinition): boolean {
		let all = false;
		if (field) {
			// editing existing member
			//it is possible all tickets types already selected
			all =
				!!field.view.ticketTypes &&
				_.isEqual(field.view.ticketTypes.sort(), [...this.customFieldService.TicketTypesKeys].sort());
			this.allTicketsIndeterminate =
				field.view.ticketTypes &&
				field.view.ticketTypes.length > 0 &&
				field.view.ticketTypes.length < this.customFieldService.TicketTypesMap.size;
		} else {
			// adding new member. select all already
			this.allTicketsIndeterminate = true; // since default is ['Tasks']
		}

		return all;
	}

	private handleLiveUpdate() {
		this.liveSubscription = defer(() =>
			this.itemFormGroup
				.get('itemInfoGroup')
				.valueChanges.pipe(
					startWith((this.itemFormGroup.get('itemInfoGroup') as UntypedFormGroup).getRawValue()),
					pairwise(),
					takeUntil(this.shutdownSubject$)
				)
		).subscribe(([prev, next]) => {
			const value = diff(prev, next);
			const update = {};
			Object.keys(value).forEach((key) => {
				if (value[key] !== null && value[key] !== undefined) {
					let changed = value[key];
					// if roles or ticketTypes (they are arrays), or listOfObjects changed and there is a difference, get the latest value
					if (key === 'ticketTypes' || key === 'roles' || key === 'listOfValues') {
						changed = next[key];
					}
					update[key] = changed;
				}
			});
			// somehow, the type change could not be reflected in prev/next pair
			if (next['type']) {
				update['type'] = next['type'];
			}
			// remove this internal information if it is there
			delete update['itemAllTicketsControl'];
			if (Object.keys(update).length > 0) {
				if (this.dropdownDefinitions && update['listOfValues'] && !update['listOfValues']['data']) {
					update['listOfValues'] = { dataType: 'string', data: update['listOfValues'] };
				}

				this.planState.customFieldsApi.localUpdateDefinition(this.item.$id, update);
			}
		});
	}

	private handleFormDataChange() {
		this.itemFormGroup
			.get('itemInfoGroup')
			.get('itemAllTicketsControl')
			.valueChanges.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((value) => {
				// select all or deselect all
				if (value) {
					this.allTicketList.selectAll();
				} else {
					this.allTicketList.deselectAll();
				}
			});

		this.itemFormGroup
			.get('itemInfoGroup')
			.get('ticketTypes')
			.valueChanges.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((value) => {
				// maintain correct stat of selectAll ticket types
				const intermediate = value && value.length === this.customFieldService.TicketTypesMap.size;
				this.itemFormGroup
					.get('itemInfoGroup')
					.get('itemAllTicketsControl')
					.setValue(intermediate, { emitEvent: false });
				this.allTicketsIndeterminate =
					value && value.length > 0 && value.length < this.customFieldService.TicketTypesMap.size;
			});

		this.itemFormGroup
			.get('itemInfoGroup')
			.get('type')
			.valueChanges.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((value) => {
				// dropdown list when type changes
				this.updateDropdownChoiceArray();
			});

		// For maximum limit error message display bug. Do not want to tie this with live update. Otherwise, a lot of unneccesary
		// localUpdateDefinition calls will happen.
		this.itemFormGroup
			.get('itemInfoGroup')
			.get('label')
			.valueChanges.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((value) => {
				this.handleMaximumLength(this.itemFormGroup.get('itemInfoGroup').get('label'), value);
			});
		this.newChoiceControl.valueChanges.pipe(takeUntil(this.shutdownSubject$)).subscribe((value) => {
			this.handleMaximumLength(this.newChoiceControl, value);
		});
	}

	/**The maximum reached error is actually working. But a bug makes it seems not to be working. The bug is:
	 * the  very first inout field editing in that dialog, whether it is field lable, adding choice input, or existing choice input,
	 * does not shown the maximum reached error even when that happened. We have to click somewhere else for it to show up. Ant after that,
	 * the maximum reached error shows up while user is typing in any of those. This is for fixing that bug
	 */
	private handleMaximumLength(control: AbstractControl, value) {
		if (!value || value.length > this.customFieldService.MaxLabelLength) {
			control.markAsTouched();
		}
	}

	public handleItemEvent(emittedData: IButtonPanelEventData) {
		this.newChoiceControl.setValue(null);

		this.fireCommitEvent(emittedData);
	}

	public selectAllRoles() {
		this.allRolesList.selectAll();
	}

	public deselectAllRoles() {
		this.allRolesList.deselectAll();
	}

	public addDropdownChoice(choice) {
		const list = [...this.dropdownDefinitions];
		list.push({
			isEnabled: true,
			label: choice.value,
			order: this.planState.customFieldsApi.getNewDropDownItemOrder(this.item.view.listOfValuesId),
			local: true, // indicating this is a local change to be saved
		});

		this.planState.customFieldsApi.localUpdateDefinition(this.item.$id, {
			type: CustomFieldType.DropDownList,
			listOfValues: {
				dataType: 'string',
				data: list,
			},
		});

		// clear input box
		choice.value = null;
	}

	public deleteDropdownChoice(index: number) {
		const list = [...this.dropdownDefinitions];
		list.splice(index, 1);

		this.planState.customFieldsApi.localUpdateDefinition(this.item.$id, {
			listOfValues: {
				dataType: 'string',
				data: list,
			},
		});
	}

	public reorderDropdownChoice(event: CdkDragDrop<string[]>, items: ICustomFieldDropDownItemDefinition[]) {
		if (event.previousIndex !== event.currentIndex) {
			this.planState.customFieldsApi.moveDropdownItem(
				this.item.view.listOfValuesId,
				event.previousIndex,
				event.currentIndex
			);

			this.itemFormGroup.markAsDirty();
		}
	}

	public getNumberOfDropdownChoiceRows(): number {
		let rows = 1;
		if (this.itemFormGroup.get('itemInfoGroup').get('listOfValues')) {
			// With the current setting, 2 grid rows can accomodate 3 dropdown list choices.
			const list = this.itemFormGroup.get('itemInfoGroup').get('listOfValues')
				.value as ICustomFieldDropDownItemDefinition[];
			rows += Math.floor(list.length / 3) * 2 + (list.length % 3);
		}

		return rows;
	}

	// wrapped this property so the template will allow a `.length` accessor when calling .get() on the parent formGroup
	protected get listOfValuesControl() {
		return this.itemFormGroup.get('itemInfoGroup').get('listOfValues') as FormArray<
			FormControl<ICustomFieldDropDownItemDefinition>
		>;
	}

	private updateDropdownChoiceArray() {
		let hasLocal = false;
		const choices = this.dropdownDefinitions;
		if (choices) {
			const saveButtonIsInitiallyEnabled = this.canSave();
			const ordinalsWereChanged =
				this.planState.customFieldsApi.fixDropdownChoiceOrdinalsIfNecessary(choices);
			if (ordinalsWereChanged && !saveButtonIsInitiallyEnabled) {
				// If the Save button was initially disabled, then auto-save these changes.
				// If the button was enabled, let the user choose when to save these changes AND the user's changes.
				this.handleItemEvent({
					type: 'Save',
					event: null,
				} as IButtonPanelEventData);
			}
		}

		const info = this.itemFormGroup.get('itemInfoGroup') as UntypedFormGroup;
		const listOfValues = info.get('listOfValues') as UntypedFormArray;

		// When we are here, user switched to a different definition or the choices of dropdownlist changed
		// Need to re-subscribe for live update to start with the inital values of the new definition/dropdown choices.
		// Also, there is no need to changes here when the choice list is updated.
		if (this.liveSubscription) {
			this.liveSubscription.unsubscribe();
		}

		if (choices && info.get('type').value === CustomFieldType.DropDownList) {
			if (listOfValues && listOfValues.length === choices.length) {
				const values = [];
				choices.forEach((choice) => {
					values.push({
						label: choice.label,
						isEnabled: choice.isEnabled,
						local: choice.local,
						id: choice.id,
						order: choice.order,
					});
				});
				listOfValues.patchValue(values, { emitEvent: false });
			} else {
				if (listOfValues) {
					info.removeControl('listOfValues');
				}
				const formArray: UntypedFormArray = this.builder.array(
					[],
					[
						// Make sure there's at least one choice:
						Validators.required,
						// Make sure that at least one choice is enabled:
						(control: AbstractControl): ValidationErrors | null => {
							const choiceControls = (control as UntypedFormArray).controls;
							if (choiceControls.length === 0) {
								// Don't complain about no controls; Validators.required handles that
								return null;
							}
							const enabledControl = choiceControls.find((ctrl) => ctrl.get('isEnabled').value);
							return enabledControl ? null : ({ allDisabled: true } as ValidationErrors);
						},
					]
				);
				choices.forEach((choice) => {
					if (choice.local) {
						hasLocal = true;
					}

					const group = this.builder.group({
						label: [
							choice.label,
							{
								validators: [
									Validators.required,
									Validators.maxLength(this.customFieldService.MaxLabelLength),
								],
							},
						],
						isEnabled: [choice.isEnabled],
						local: [choice.local],
						id: [choice.id],
						order: choice.order,
					});
					formArray.push(group);
					// fix maximum error message bug
					group
						.get('label')
						.valueChanges.pipe(takeUntil(this.shutdownSubject$))
						.subscribe((value) => {
							this.handleMaximumLength(group.get('label'), value);
						});
				});
				info.addControl('listOfValues', formArray);
			}
		} else {
			if (listOfValues) {
				info.removeControl('listOfValues');
			}
		}
		if (hasLocal) {
			this.itemFormGroup.markAsDirty();
		}
		this.changeDetectorRef.detectChanges();

		this.handleLiveUpdate();
	}
}
